import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, map, distinctUntilChanged, tap, catchError, throwError } from 'rxjs';
import { ApiService } from '../api/api.service';
import { StatisticsAvailabilityResponse } from '../../models/statistics-availability.model';
import { safeReadJson, safeWriteJson } from '../../utils/storage';

export type StatisticsGranularity = 'monthly' | 'daily' | 'hourly' | null;

export interface FilterState {
  projectId: string | null;
  timezone: string;
  availability: StatisticsAvailabilityResponse | null;
  selectedYear: number | null;
  selectedMonth: number | null;
  selectedDay: number | null;
  granularity: StatisticsGranularity;
  loadingAvailability: boolean;
  availabilityError: string | null;
}

const DEFAULT_TIMEZONE = 'Europe/Madrid';

@Injectable({
  providedIn: 'root'
})
export class FilterStateService {
  private readonly stateSubject = new BehaviorSubject<FilterState>({
    projectId: null,
    timezone: DEFAULT_TIMEZONE,
    availability: null,
    selectedYear: null,
    selectedMonth: null,
    selectedDay: null,
    granularity: null,
    loadingAvailability: false,
    availabilityError: null
  });

  readonly state$ = this.stateSubject.asObservable();
  readonly availableYears$ = this.state$.pipe(
    map((state) => state.availability?.years.map((entry) => entry.year) ?? [])
  );
  readonly availableMonths$ = this.state$.pipe(
    map((state) => {
      if (state.selectedYear === null || state.availability === null) {
        return [];
      }

      const yearEntry = state.availability.years.find((entry) => entry.year === state.selectedYear);
      return yearEntry?.months.map((month) => month.month) ?? [];
    })
  );
  readonly availableDays$ = this.state$.pipe(
    map((state) => {
      if (state.selectedYear === null || state.selectedMonth === null || state.availability === null) {
        return [];
      }

      const yearEntry = state.availability.years.find((entry) => entry.year === state.selectedYear);
      const monthEntry = yearEntry?.months.find((month) => month.month === state.selectedMonth);

      return monthEntry?.days ?? [];
    })
  );
  readonly granularity$ = this.state$.pipe(
    map((state) => state.granularity),
    distinctUntilChanged()
  );

  private readonly STORAGE_KEY = 'everest.filter-state.v1';

  constructor(private readonly apiService: ApiService) {
    this.restoreFromStorage();
  }

  private restoreFromStorage(): void {
    const stored = safeReadJson<Partial<Pick<FilterState, 'projectId' | 'timezone' | 'selectedYear' | 'selectedMonth' | 'selectedDay'>>>(
      this.STORAGE_KEY
    );
    if (!stored) {
      return;
    }

    this.patchState({
      projectId: typeof stored.projectId === 'string' ? stored.projectId : null,
      timezone: typeof stored.timezone === 'string' && stored.timezone.trim().length > 0 ? stored.timezone : DEFAULT_TIMEZONE,
      selectedYear: typeof stored.selectedYear === 'number' ? stored.selectedYear : null,
      selectedMonth: typeof stored.selectedMonth === 'number' ? stored.selectedMonth : null,
      selectedDay: typeof stored.selectedDay === 'number' ? stored.selectedDay : null
    });
  }

  loadAvailability(projectId: string, timezone: string = DEFAULT_TIMEZONE): Observable<StatisticsAvailabilityResponse> {
    this.patchState({
      projectId,
      timezone,
      loadingAvailability: true,
      availabilityError: null
    });

    return this.apiService.getStatisticsAvailability(projectId, timezone).pipe(
      tap((availability) => {
        const { selectedYear, selectedMonth, selectedDay } = this.coerceSelectionToAvailability(
          availability,
          this.stateSubject.getValue().selectedYear,
          this.stateSubject.getValue().selectedMonth,
          this.stateSubject.getValue().selectedDay
        );

        this.patchState({
          availability,
          selectedYear,
          selectedMonth,
          selectedDay,
          loadingAvailability: false,
          availabilityError: null
        });
      }),
      catchError((error: unknown) => {
        const message =
          typeof error === 'object' &&
          error !== null &&
          'error' in error &&
          typeof (error as { error?: { message?: string } }).error?.message === 'string'
            ? (error as { error: { message: string } }).error.message
            : 'Failed to load availability';

        this.patchState({
          availability: null,
          loadingAvailability: false,
          availabilityError: message
        });

        return throwError(() => error);
      })
    );
  }

  selectYear(year: number | null): void {
    this.patchState({
      selectedYear: this.normalizeYear(year),
      selectedMonth: null,
      selectedDay: null
    });
  }

  selectMonth(month: number | null): void {
    this.patchState({
      selectedMonth: this.normalizeMonth(month),
      selectedDay: null
    });
  }

  selectDay(day: number | null): void {
    this.patchState({
      selectedDay: this.normalizeDay(day)
    });
  }

  clearSelection(): void {
    this.patchState({
      selectedYear: null,
      selectedMonth: null,
      selectedDay: null
    });
  }

  getSnapshot(): FilterState {
    return this.stateSubject.getValue();
  }

  private patchState(patch: Partial<Omit<FilterState, 'granularity'>>): void {
    const currentState = this.stateSubject.getValue();
    const nextState = {
      ...currentState,
      ...patch
    };

    const finalState: FilterState = {
      ...nextState,
      granularity: this.resolveGranularity(nextState.selectedYear, nextState.selectedMonth, nextState.selectedDay)
    };

    this.stateSubject.next(finalState);

    // Persist only the fields we need to restore navigation context after a full reload.
    safeWriteJson(this.STORAGE_KEY, {
      projectId: finalState.projectId,
      timezone: finalState.timezone,
      selectedYear: finalState.selectedYear,
      selectedMonth: finalState.selectedMonth,
      selectedDay: finalState.selectedDay
    });
  }

  private resolveGranularity(
    year: number | null,
    month: number | null,
    day: number | null
  ): StatisticsGranularity {
    if (year === null) {
      return null;
    }

    if (day !== null) {
      return 'hourly';
    }

    if (month !== null) {
      return 'daily';
    }

    return 'monthly';
  }

  private normalizeYear(year: number | null): number | null {
    if (year === null) {
      return null;
    }

    const availableYears = this.stateSubject.getValue().availability?.years.map((entry) => entry.year) ?? [];
    return availableYears.includes(year) ? year : null;
  }

  private normalizeMonth(month: number | null): number | null {
    if (month === null) {
      return null;
    }

    const state = this.stateSubject.getValue();
    if (state.selectedYear === null) {
      return null;
    }

    const yearEntry = state.availability?.years.find((entry) => entry.year === state.selectedYear);
    const availableMonths = yearEntry?.months.map((entry) => entry.month) ?? [];

    return availableMonths.includes(month) ? month : null;
  }

  private normalizeDay(day: number | null): number | null {
    if (day === null) {
      return null;
    }

    const state = this.stateSubject.getValue();
    if (state.selectedYear === null || state.selectedMonth === null) {
      return null;
    }

    const yearEntry = state.availability?.years.find((entry) => entry.year === state.selectedYear);
    const monthEntry = yearEntry?.months.find((entry) => entry.month === state.selectedMonth);
    const availableDays = monthEntry?.days ?? [];

    return availableDays.includes(day) ? day : null;
  }

  private coerceSelectionToAvailability(
    availability: StatisticsAvailabilityResponse | null,
    year: number | null,
    month: number | null,
    day: number | null
  ): { selectedYear: number | null; selectedMonth: number | null; selectedDay: number | null } {
    if (!availability || !Array.isArray(availability.years) || availability.years.length === 0) {
      return { selectedYear: null, selectedMonth: null, selectedDay: null };
    }

    const availableYears = availability.years.map((entry) => entry.year);
    const nextYear = typeof year === 'number' && availableYears.includes(year) ? year : null;
    if (nextYear === null) {
      return { selectedYear: null, selectedMonth: null, selectedDay: null };
    }

    const yearEntry = availability.years.find((entry) => entry.year === nextYear);
    const availableMonths = yearEntry?.months.map((entry) => entry.month) ?? [];
    const nextMonth = typeof month === 'number' && availableMonths.includes(month) ? month : null;
    if (nextMonth === null) {
      return { selectedYear: nextYear, selectedMonth: null, selectedDay: null };
    }

    const monthEntry = yearEntry?.months.find((entry) => entry.month === nextMonth);
    const availableDays = monthEntry?.days ?? [];
    const nextDay = typeof day === 'number' && availableDays.includes(day) ? day : null;

    return { selectedYear: nextYear, selectedMonth: nextMonth, selectedDay: nextDay };
  }
}
